'use client';

import React from 'react';
import Image from 'next/image';
import { trackEvent } from '@/lib/analytics';
import NotifyMe from './NotifyMe';

interface DownloadCTAClientProps {
  appStoreUrl: string;
  playStoreUrl: string;
}

export default function DownloadCTAClient({ appStoreUrl, playStoreUrl }: DownloadCTAClientProps) {
  return (
    <div className="grid items-center gap-5 rounded-3xl border border-white/25 bg-white/10 p-5 backdrop-blur-sm sm:grid-cols-[1fr_auto] sm:gap-4 sm:p-6">
      <div className="flex flex-col items-center gap-4 sm:items-start">
        <a
          href={playStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('download_play_store_click')}
          className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-white px-5 py-4 text-left text-foreground shadow-lg transition-colors hover:bg-white/90 sm:justify-start"
          aria-label="Download planB on Google Play"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3.18 23.76c.3.17.64.22.98.14l12.82-7.4-2.82-2.82-10.98 10.08zM.36 1.52C.13 1.86 0 2.3 0 2.84v18.32c0 .54.13.98.36 1.32l.07.07 10.27-10.27v-.24L.43 1.45l-.07.07zM20.66 10.27l-2.78-1.6-3.17 3.17 3.17 3.17 2.8-1.62c.8-.46.8-1.21-.02-1.72zM4.16.22L17 7.62l-2.82 2.82L4.16.22z" />
          </svg>
          <span>
            <span className="block text-xs text-muted-foreground">Download now on</span>
            <span className="block text-sm font-bold">Google Play</span>
          </span>
        </a>

        <span className="rounded-full border border-white/35 bg-white/10 px-4 py-2 text-sm font-medium text-white">
          iOS app coming soon
        </span>

        {!appStoreUrl && (
          <div className="pt-2 text-center sm:text-left">
            <p className="mb-3 text-sm font-semibold text-white">Want an iOS launch update?</p>
            <NotifyMe variant="cta" />
          </div>
        )}

        {appStoreUrl && (
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('download_app_store_click')}
            className="text-sm font-medium text-white underline underline-offset-4"
          >
            Also available on the App Store
          </a>
        )}
      </div>

      <a
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('download_play_store_click')}
        className="mx-auto flex w-fit flex-col items-center"
        aria-label="Scan QR code to download planB from Google Play"
      >
        <Image
          src="/assets/images/planb-play-store-qr.png"
          alt="QR code linking to planB on Google Play"
          width={132}
          height={132}
          className="h-32 w-32"
        />
        <span className="mt-2 text-xs font-semibold">Scan to download</span>
      </a>
    </div>
  );
}
